/**
 * @file LogEntryNote.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-02
 * @modified 2026-10-02
 *
 * A log entry's note to the reader, and the prompt to act on it.
 *
 * A writer to public.sync_history may put plain text for a person in its
 * details: `note` (paragraphs separated by a blank line) and `fix_prompt`
 * (exact text to give Claude). The first writer is the nightly credit
 * allocation check (services/creditAllocationCheck.js), whose warning reminds
 * Cam that a fix is planned and what to ask for to have it built.
 *
 * The log otherwise shows details only as a JSON block, where a note arrives
 * as one escaped string full of \n. This renders it as text, and the prompt as
 * a quote with a copy button. Shared by SystemLogAlerts (the widget's alert
 * view and the "Issues Logged" modal) and SystemLogViewer.
 */

import React, { useState, useEffect, useRef } from 'react';
import './LogEntryNote.css';

const isText = (v) => typeof v === 'string' && v.trim() !== '';

/** { note, prompt } from a log entry's details, or null when it has neither. */
export function noteOf(details) {
  if (!details || typeof details !== 'object') return null;
  const note = isText(details.note) ? details.note : null;
  const prompt = isText(details.fix_prompt) ? details.fix_prompt : null;
  return note || prompt ? { note, prompt } : null;
}

/** The details without the two keys this component renders, for the JSON block. */
export function withoutNote(details) {
  if (!noteOf(details)) return details;
  const { note, fix_prompt: prompt, ...rest } = details;
  return rest;
}

function LogEntryNote({ note, prompt }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!note && !prompt) return null;
  const paragraphs = note ? note.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean) : [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard refused; the prompt is on screen to select */ }
  };

  return (
    <div className="log-entry-note">
      <span className="material-symbols-rounded len-icon" aria-hidden="true">lightbulb</span>
      <div className="len-body">
        {paragraphs.map((p, i) => <p key={i} className="len-text">{p}</p>)}
        {prompt && (
          <div className="len-prompt">
            <span className="len-prompt-text">{prompt}</span>
            <button
              type="button"
              className="btn-icon len-copy"
              onClick={copy}
              title={copied ? 'Copied' : 'Copy the prompt'}
              aria-label={copied ? 'Copied' : 'Copy the prompt'}
            >
              <span className="material-symbols-rounded">{copied ? 'check' : 'content_copy'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LogEntryNote;
